import React, { useState, useMemo, useRef, useEffect } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import 'ag-grid-enterprise';
import ConfirmDeleteModal from "../confirmdeletemodal";
import loaderGif from "../../../assets/loader.gif";
import api from '../../../config/axiosConfig';


const BookingsList = () => {
    const [formData, setFormData] = useState({
        bookingId: null,  // Track if it's a new entry or an edit
        accountId: '',
        checkInDate: '',
        checkOutDate: '',
        days: '',
        total: '',
        discount: '',
        advancedPayment: '',
        isactive: true,
        createddate: '',
        modifieddate: '',
        createdby: ''
    });

    const [rowData, setRowData] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false); // To track if we are in edit mode
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loader, setLoader] = useState(false);

    const getData = async () => {
        try {
            const response = await api.get('/bookings');
            setRowData(response.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        getData();
    }, []);
    const gridRef = useRef();

    // Column definitions for Ag-Grid
    const columnDefs = [

        { headerName: 'ID', field: 'bookingId', sort: "desc", filter: 'agNumberColumnFilter' },
        { headerName: 'Reference', field: 'referenceCode', filter: 'agNumberColumnFilter' },
        { headerName: 'Account ID', field: 'accountId', filter: 'agNumberColumnFilter' },
        { headerName: 'Account Name', field: 'name', filter: 'agTextColumnFilter' },
        {
            headerName: 'Check-In Date', field: 'checkInDate', filter: 'agDateColumnFilter',
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return date.toLocaleDateString('en-CA'); // Format the date
            },
        },
        {
            headerName: 'Check-Out Date', field: 'checkOutDate', filter: 'agDateColumnFilter',
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return date.toLocaleDateString('en-CA'); // Format the date
            },
        },
        { headerName: 'Days', field: 'days', filter: 'agNumberColumnFilter' },
        { headerName: 'Total', field: 'total', filter: 'agNumberColumnFilter' },
        { headerName: 'Discount', field: 'discount', filter: 'agNumberColumnFilter' },
        { headerName: 'Advanced Payment', field: 'advancedPayment', filter: 'agNumberColumnFilter' },
        { headerName: 'Is Active', field: 'isactive' },
        { headerName: 'Created By', field: 'createdby', filter: 'agTextColumnFilter' },
        { headerName: 'Created At', field: 'createddate', filter: 'agDateColumnFilter' },
        { headerName: 'Updated At', field: 'modifieddate', filter: 'agDateColumnFilter' },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: (params) => (
                <Button
                    variant="danger"
                    className="btn-sm"
                    // onClick={() => handleDelete(params.data)}
                >
                    <i className="bi bi-trash3"></i>
                </Button>
            ),
        },
    ];

    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                }
            ],
            defaultToolPanel: '',
        };
    }, []);

    // Server-Side Datasource
    const datasource = {
        getRows: async function (params) {
            try {
                const { request } = params;

                const response = await api.post('/bookings/ag', { ...request });

                const { rows, lastRow } = response.data;

                // Send data back to AG Grid
                params.successCallback(rows, lastRow);
            } catch (error) {
                console.error("Error fetching rows:", error);
                params.failCallback();
            }
        }
    };

    const gridOptions = {
        rowModelType: 'serverSide',
        columnDefs: columnDefs,
        rowHeight: 60,

        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            filterParams: {
                buttons: ["reset"],
                closeOnApply: true,
            },
        },

        sideBar: sideBar,
        rowGroupPanelShow: 'always',
        rowSelection: 'single',
        columnHoverHighlight: true,
        suppressRowGroupHidesColumns: true,
        cacheBlockSize: 20,
    }

    // Attach the datasource when the grid is ready
    const onGridReady = (params) => {
        params.api.setServerSideDatasource(datasource);
    };

    // Row selection handler
    const onRowSelected = (event) => {
        if (event.node.isSelected()) {
            const selectedRow = event.data;

            const formattedDate = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toLocaleDateString('en-CA'); // Returns "DD/MM/YYYY"
            };
            setFormData({
                bookingId: selectedRow.bookingId,
                accountId: selectedRow.accountId,
                checkInDate: formattedDate(selectedRow.checkInDate),
                checkOutDate: formattedDate(selectedRow.checkOutDate),
                days: selectedRow.days,
                total: selectedRow.total,
                discount: selectedRow.discount,
                advancedPayment: selectedRow.advancedPayment,
                isactive: selectedRow.isactive
            });

            setIsEditMode(true);  // Switch to edit mode
            setSelectedRow(selectedRow)
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle form submission
    // Handle form submission for adding/updating media
    const handleAddData = async (e) => {
        e.preventDefault();
        setLoader(true);

        // Prepare data for media entry
        const bookingsData = {
            accountId: formData.accountId,
            checkInDate: formData.checkInDate,
            checkOutDate: formData.checkOutDate,
            days: formData.days,
            total: formData.total,
            discount: formData.discount,
            advancedPayment: formData.advancedPayment,
            isactive: formData.isactive,
            createdat: new Date(),

        };
        // Determine whether to update or create a new entry
        // const url = isEditMode ?   `/bookings/${formData.bookingId}` : '/bookings';
        // const request =  isEditMode ? api.put(url, bookingsData) : api.post(url, bookingsData) ;

        const url = `/bookings/${formData.bookingId}`;
        const request = api.put(url, bookingsData);

        try {
            const response = await request;

            // Clear form fields and reset edit mode
            setFormData({ id: null, accountId: '', checkInDate: '', checkOutDate: '', days: '', total: '', discount: '', advancedPayment: '', isactive: true });
            setIsEditMode(false);
            setLoader(false);

            gridRef.current.api.refreshServerSideStore({ purge: true });

        } catch (error) {
            console.error("Error adding/updating bookings:", error);
            setLoader(false);
        }
    };



    // Reset form for new entry
    const handleNewEntry = () => {
        setFormData({ id: null, accountId: '', checkInDate: '', checkOutDate: '', days: '', total: '', discount: '', advancedPayment: '', isactive: true });
        // setFiles([]);
        setIsEditMode(false);  // Switch to add mode
    };

    const handleDelete = () => {
        setShowModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/bookings/${selectedRow.bookingId}`);
            setShowModal(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });
        } catch (error) {
            console.error('Error deleting booking:', error);
        }
    };


    return (
        <>
            <Container fluid>
                <Row className="mt-4">
                    <h6 className="text-start">Bookings List</h6>
                    <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            gridOptions={gridOptions}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                </Row>
                <Row className="mt-4 border shadow p-4">
                    {loader && (
                        <div className="loader-overlay">
                            <img src={loaderGif} alt="Loading..." className="loader" />
                        </div>
                    )}
                    <Col md={12}>
                        <h6 className="text-start">{"Edit Booking"}</h6>
                        {/* <h6 className="text-start">{isEditMode ? "Edit Booking" : "Add Booking"}</h6> */}

                        <Form onSubmit={handleAddData}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="me-3">Account ID:</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="accountId"
                                            placeholder="Enter Account ID"
                                            value={formData.accountId}
                                            onChange={handleChange}
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Check
                                            type="checkbox"
                                            name="isactive"
                                            label="Is Active"
                                            checked={formData.isactive}
                                            onChange={handleChange}
                                            className="ms-4"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row><br />
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Check-In Date</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="date"
                                                name="checkInDate"
                                                placeholder="Select Check-In Date"
                                                value={formData.checkInDate}
                                                onChange={handleChange}
                                                readOnly
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Check-Out Date</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="date"
                                                name="checkOutDate"
                                                placeholder="Select Check-Out Date"
                                                value={formData.checkOutDate}
                                                onChange={handleChange}
                                                readOnly
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Days</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="days"
                                            placeholder="Calculated Days"
                                            value={formData.days}
                                            onChange={handleChange}
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Total</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                name="total"
                                                placeholder="Enter Total"
                                                value={formData.total}
                                                onChange={handleChange}
                                                readOnly
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Discount</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                name="discount"
                                                placeholder="Enter Discount"
                                                value={formData.discount}
                                                onChange={handleChange}
                                                readOnly
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Advanced Payment</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="advancedPayment"
                                                placeholder="Calculated Advanced Payment"
                                                value={formData.advancedPayment}
                                                onChange={handleChange}
                                                readOnly
                                            />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Button variant="primary" type="submit">
                                {"Update"}
                                {/* {isEditMode ? "Update" : "Add"} */}

                            </Button>
                            {/* {isEditMode ?
                                <Button variant="secondary" className="ms-3" onClick={handleNewEntry}>
                                    Create New Entry
                                </Button>
                                : ''
                            } */}
                        </Form>
                    </Col>
                </Row>
            </Container >

            {/* Modal for confirming deletion */}
            < ConfirmDeleteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmDelete}
                title="Confirm Deletion"
                body={`Are you sure you want to delete ${selectedRow?.name}?`}
                confirmText="Delete"
            />




        </>
    );
};

export default BookingsList;